import {DataTestAttrs, getTestProps} from '@benchling/js-test-utils';
import {Color as NucleusColor} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import produce from 'immer';
import React, {useState} from 'react';
import {Node, Handle, Position, useNodeId, useReactFlow} from 'reactflow';

import {
  makeDataFlowEdgeHandle,
  HANDLE_TYPE_NODE,
  HANDLE_TYPE_FIELD,
  HANDLE_DIRECTION_SOURCE,
  HANDLE_DIRECTION_TARGET,
  FIELD_TYPE_INPUT,
  FIELD_TYPE_OUTPUT,
  HandleDirection,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/flowchartIdentifierUtils';
import {
  TaskSchemaNodeSection,
  TaskSchemaNodeSectionType,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {
  TaskSchemaNodeData,
  TaskSchemaNodeRowData,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import {FlowchartNodeChangeType} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorUtils';
import {FlowchartConfigWithMetadata} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/redux/flowchartConfigAutosaveManagerCallbacks';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import Option, {OptionProps} from 'ui-library/menus/Option';
import {ROW_HANDLE_STYLES} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNodeDesignConstants';
import {BenchlingNodeV2} from 'ui-library/reactflow/nodes/BenchlingNode/v2/BenchlingNode';

import {HandlesContext, HandlesContextValue} from '../HandlesContext';
import {LockedContext} from '../LockedContext';

export type TaskSchemaNodeRowRendererProps = TaskSchemaNodeRowData;

/**
 * Determines whether a handle should be hidden based on node type, handle direction, and drag state.
 *
 * @package - exported for testing only
 * @param rowData - The row data containing section information and flowchart task status
 * @param handleDirection - The direction of the handle (source or target)
 * @param connectionDragState - Current connection drag state from HandlesContext
 * @returns true if the handle should be hidden, false if it should be visible
 */
export function shouldHideHandle(
  rowData: TaskSchemaNodeRowRendererProps,
  handleDirection: HandleDirection,
  connectionDragState: HandlesContextValue['connectionDragState']
): boolean {
  const isCurrentNodeFlowchartInputNode =
    rowData.sectionName === TaskSchemaNodeSection.INPUTS && rowData.isFlowchartTask;
  const isCurrentNodeFlowchartOutputNode =
    rowData.sectionName === TaskSchemaNodeSection.OUTPUTS && rowData.isFlowchartTask;

  if (isCurrentNodeFlowchartInputNode && handleDirection === HANDLE_DIRECTION_TARGET) {
    return true;
  } else if (isCurrentNodeFlowchartOutputNode && handleDirection === HANDLE_DIRECTION_SOURCE) {
    return true;
  }

  if (connectionDragState?.isDragging && connectionDragState.sourceHandleInfo) {
    const sourceInfo = connectionDragState.sourceHandleInfo;

    // Always hide field handles if dragging from a node handle (control flow handle)
    if (sourceInfo.handleCategory === HANDLE_TYPE_NODE) {
      return true;
    }

    if (sourceInfo.handleCategory === HANDLE_TYPE_FIELD) {
      if (sourceInfo.direction === handleDirection) {
        return true;
      }

      const currentFieldType =
        rowData.sectionType === TaskSchemaNodeSectionType.INPUTS ? FIELD_TYPE_INPUT : FIELD_TYPE_OUTPUT;

      if (currentFieldType === FIELD_TYPE_OUTPUT) {
        // Allow connections to output fields only when target is an output node
        return !isCurrentNodeFlowchartOutputNode;
      }

      return false;
    }

    return false;
  }

  // We aren't dragging, we should not show target handles
  return handleDirection === HANDLE_DIRECTION_TARGET;
}

/**
 * Checks if the row has handles connected to it, returns booleans for the source and target handles.
 */
function doesRowHaveHandles(
  clientId: string,
  sectionType: TaskSchemaNodeSectionType,
  handlesInUse: Array<string>
): {isSourceConnected: boolean; isTargetConnected: boolean} {
  const sourceRowHandleId = makeDataFlowEdgeHandle(clientId, sectionType, 'source');
  const targetRowHandleId = makeDataFlowEdgeHandle(clientId, sectionType, 'target');
  const isSourceConnected = handlesInUse.filter((handle) => handle === sourceRowHandleId).length > 0;
  const isTargetConnected = handlesInUse.filter((handle) => handle === targetRowHandleId).length > 0;
  return {isSourceConnected, isTargetConnected};
}

/**
 * Handles used to draw edges between fields on two nodes.
 */
function RowHandle(props: {
  rowData: TaskSchemaNodeRowRendererProps;
  handleDirection: HandleDirection;
  isConnected: boolean;
  isHovered: boolean;
  dataTestAttrs?: DataTestAttrs;
}): JSX.Element {
  const handlesContext = HandlesContext.useContext();
  const locked = LockedContext.useContext();
  const rowHandleId = makeDataFlowEdgeHandle(
    props.rowData.clientId,
    props.rowData.sectionType,
    props.handleDirection
  );

  const isDragInProgress = handlesContext.connectionDragState?.isDragging;

  let isVisible: boolean = false;
  if (props.isConnected) {
    isVisible = true;
  } else if (!locked && (props.isHovered || isDragInProgress)) {
    isVisible = !shouldHideHandle(props.rowData, props.handleDirection, handlesContext.connectionDragState);
  }

  return (
    <Handle
      className={css(
        ROW_HANDLE_STYLES.rowHandle,
        props.handleDirection === HANDLE_DIRECTION_TARGET
          ? ROW_HANDLE_STYLES.leftHandle
          : ROW_HANDLE_STYLES.rightHandle,
        !isVisible && ROW_HANDLE_STYLES.hidden,
        locked && ROW_HANDLE_STYLES.lockedRowHandle
      )}
      type={props.handleDirection}
      position={props.handleDirection === HANDLE_DIRECTION_TARGET ? Position.Left : Position.Right}
      id={rowHandleId}
      {...getTestProps(props.dataTestAttrs)}
    >
      <div className={css(ROW_HANDLE_STYLES.handleDot)} />
    </Handle>
  );
}

/**
 * Note that react flow v12 (@xyflow/react) would allow us to use the `useNodeData` hook to get the data for the
 * current node. Can replace once we upgrade from react flow v11.
 */
function useCurrentNodeData(): TaskSchemaNodeData | null {
  const currentNodeId = useNodeId();
  const reactFlow = useReactFlow();
  if (currentNodeId == null) {
    return null;
  }
  const currentNode: Node<TaskSchemaNodeData> | undefined = reactFlow.getNode(currentNodeId);
  if (currentNode == null) {
    return null;
  }
  return currentNode.data;
}

/**
 * Render a field in the task schema node
 */
export function TaskSchemaNodeRowRenderer(props: TaskSchemaNodeRowRendererProps): JSX.Element {
  const [isHovered, setIsHovered] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const nodeData = useCurrentNodeData();
  const handlesContext = HandlesContext.useContext();
  const {isSourceConnected, isTargetConnected} = doesRowHaveHandles(
    props.clientId,
    props.sectionType,
    handlesContext.handlesInUse
  );
  const locked = LockedContext.useContext();

  const leftHandle = (
    <RowHandle
      rowData={props}
      isConnected={isTargetConnected}
      handleDirection={HANDLE_DIRECTION_TARGET}
      isHovered={isHovered}
      dataTestAttrs={{
        component: 'TaskSchemaNodeRowRenderer',
        element: 'targetHandle',
        key: props.fieldName,
      }}
    />
  );

  const rightHandle = (
    <RowHandle
      rowData={props}
      isConnected={isSourceConnected}
      handleDirection={HANDLE_DIRECTION_SOURCE}
      isHovered={isHovered}
      dataTestAttrs={{
        component: 'TaskSchemaNodeRowRenderer',
        element: 'sourceHandle',
        key: props.fieldName,
      }}
    />
  );

  const contextMenuOptions: Array<OptionProps<void>> = [];
  if (!locked && props.sectionType === TaskSchemaNodeSectionType.MAP_FROM_START && nodeData) {
    contextMenuOptions.push({
      icon: 'trash-can',
      children: 'Remove', // The title of the option
      shouldEllipsize: false,
      onSelect: () => {
        const newNodeData = produce(nodeData, (draftNode) => {
          draftNode.sections = draftNode.sections.map((section) => {
            if (section.sectionType === TaskSchemaNodeSectionType.MAP_FROM_START) {
              section.sectionRows = section.sectionRows.filter((row) => row.id !== props.id);
              return section;
            }
            return section;
          });
        });
        nodeData?.onUpdateNodeData((flowchartConfig: FlowchartConfigWithMetadata) => [
          {id: nodeData.id, data: newNodeData, type: FlowchartNodeChangeType.REPLACE},
        ]);
      },
      dataTestAttrs: {},
      isDisabled: isSourceConnected,
      tooltipTitle: isSourceConnected ? 'Delete all edges to remove this field' : null,
    });
  }

  return (
    <div
      className={css(
        ROW_STYLES.wrapper,
        nodeData != null && ROW_STYLES.clickableRow,
        !props.isSelected && isHovered && ROW_STYLES.hoveredRow,
        props.isSelected && ROW_STYLES.selectedRow
      )}
      onMouseEnter={() => {
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        setIsHovered(false);
      }}
      onClick={() => {
        if (nodeData != null) {
          nodeData.onFieldClick(nodeData.id, props.clientId);
        }
      }}
      onContextMenu={(e) => {
        if (contextMenuOptions.length === 0) {
          return;
        }
        setIsMenuOpen(true);
        e.preventDefault();
        e.stopPropagation();
      }}
      {...getTestProps({
        component: 'TaskSchemaNodeRowRenderer',
        element: 'root',
        key: props.id,
      })}
    >
      <Dropdown
        isOpen={isMenuOpen}
        onToggle={() => {
          if (isMenuOpen) {
            setIsMenuOpen(false);
          }
        }}
        menu={
          <Menu>
            {contextMenuOptions.map((option) => (
              <Option key={option.icon} {...option} />
            ))}
          </Menu>
        }
      >
        <BenchlingNodeV2.Standard.SectionRow
          primaryText={props.fieldName}
          icon={props.fieldIcon}
          leftHandle={leftHandle}
          rightHandle={rightHandle}
          status={props.status}
          statusDescription={props.statusDescription}
          secondaryIcon={props.secondaryIcon}
        />
      </Dropdown>
    </div>
  );
}

export const ROW_STYLES = StyleSheet.create({
  wrapper: {
    width: '100%',
    position: 'relative',
  },
  clickableRow: {
    cursor: 'pointer',
  },
  hoveredRow: {
    backgroundColor: NucleusColor.GRAY_50,
  },
  selectedRow: {
    backgroundColor: NucleusColor.BLUE_50,
    color: NucleusColor.BLUE_450,
  },
});
